import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { FaDiscord } from 'react-icons/fa';

const INK = {
  primaria: 'rgba(255, 255, 255, 0.92)',
  secundaria: 'rgba(255, 255, 255, 0.56)',
  borde: 'rgba(255, 255, 255, 0.10)',
  bordeFuerte: 'rgba(255, 255, 255, 0.18)',
  hover: 'rgba(255, 255, 255, 0.09)',
};

// Blurple actual de Discord. El codigo traia rgba(114,137,218), que es el
// blurple viejo que la marca dejo de usar en 2021.
const DISCORD = '#5865F2';

// Mismo tratamiento de campo que el formulario de coleccion. Antes esto era un
// TextField `standard` con `disableUnderline`: se elegia el variant que trae
// subrayado para despues apagarlo, y el resultado era una caja de fondo 3% sin
// borde. Enfocada pasaba a 5%: cinco valores sobre 255, invisible con teclado.
const campoSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.9375rem',
    '& fieldset': {
      borderColor: INK.borde,
      transition: 'border-color 160ms ease',
    },
    '&:hover fieldset': { borderColor: INK.bordeFuerte },
    // Color y grosor explicitos: dejar solo el grosor heredaba el borderColor
    // de la regla de arriba y el campo enfocado quedaba igual al de reposo.
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.9375rem',
    color: INK.secundaria,
    '&.Mui-focused': { color: 'primary.main' },
    // Los dos campos son obligatorios, asi que el asterisco no distingue uno
    // de otro: solo repite ruido en cada etiqueta. El input conserva
    // `required` para aria-required y la validacion vive en el submit.
    '& .MuiFormLabel-asterisk': { display: 'none' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .MuiOutlinedInput-root fieldset': { transition: 'none' },
  },
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discordLoading, setDiscordLoading] = useState(false);
  const [error, setError] = useState(null);

  const { user, signIn, signInWithDiscord } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/gallery" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Enter your email and password');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { error } = await signIn(email, password);
      if (error) throw error;

      navigate('/gallery');
    } catch (error) {
      console.error('Error signing in:', error);
      setError(error.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscordLogin = async () => {
    try {
      setDiscordLoading(true);
      setError(null);

      await signInWithDiscord();
      // El redirect de OAuth se encarga de la navegacion.
    } catch (error) {
      console.error('Error signing in with Discord:', error);
      setError(error.message || 'Failed to sign in with Discord. Please try again.');
      setDiscordLoading(false);
    }
  };

  const ocupado = loading || discordLoading;

  return (
    // Sin `background: #000` propio: era la unica pantalla con canvas negro
    // puro mientras el resto de la app corre sobre #121212.
    <Box
      sx={{
        // En iOS, 100vh no se achica cuando sube el teclado: el formulario
        // queda centrado en un area mas alta que la visible y el campo que
        // estas escribiendo se va detras del teclado. dvh si sigue al viewport.
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        px: 2,
        py: 6,
      }}
    >
      {/* Antes esto era un Paper con blur sobre un fondo solido —no habia nada
          que desenfocar— envuelto en un Fade de 1500ms. Un segundo y medio
          para ver un formulario de dos campos. La caja no separaba de nada:
          es la unica cosa en pantalla. */}
      <Box sx={{ width: '100%', maxWidth: '380px' }}>
        <Typography
          component="h1"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            // Mismo morado que el logo del header. Aca decia #697EC8: la misma
            // marca con dos colores distintos segun la pantalla.
            color: 'primary.main',
          }}
        >
          Gallery.gg
        </Typography>
        <Typography sx={{ mt: 1, mb: 4, fontSize: '0.9375rem', color: INK.secundaria }}>
          Sign in to see the collections.
        </Typography>

        {error && (
          // El resto de la app usa Alert para esto. Aca era un Box con un borde
          // rojo al 50% y el texto en blanco: un error que no se leia como error.
          <Alert severity="error" sx={{ mb: 3, fontSize: '0.875rem' }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {/* Los campos eran dos placeholders sin label: sin nombre accesible,
              sin autocomplete y sin name, asi que el gestor de contrasenas no
              podia guardarlos ni rellenarlos. Y al escribir desaparecia la
              unica pista de que campo era cual. */}
          <TextField
            fullWidth
            id="login-email"
            name="email"
            type="email"
            label="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={ocupado}
            sx={{ ...campoSx, mb: 2 }}
          />

          <TextField
            fullWidth
            id="login-password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={ocupado}
            sx={campoSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    // El icono se queda chico, el blanco de toque no: con
                    // size="small" el boton medía 30px y en el telefono se
                    // toca seguido. 44 es el minimo comodo con el pulgar.
                    sx={{
                      width: 44,
                      height: 44,
                      color: INK.secundaria,
                      '&:hover': { color: INK.primaria },
                    }}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Antes las dos acciones eran botones de texto con borde, y la de
              Discord llevaba el borde mas fuerte de las dos: la alternativa
              pesaba mas que el camino por defecto. */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disableElevation
            disabled={ocupado}
            sx={{ mt: 3, py: 1.25, fontSize: '0.9375rem', fontWeight: 500 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign in'}
          </Button>

          <Divider
            sx={{
              my: 3,
              borderColor: INK.borde,
              '&::before, &::after': { borderColor: INK.borde },
              '& .MuiDivider-wrapper': {
                fontSize: '0.8125rem',
                color: INK.secundaria,
              },
            }}
          >
            or
          </Divider>

          <Button
            onClick={handleDiscordLogin}
            fullWidth
            variant="outlined"
            disabled={ocupado}
            // El icono conserva el color de la marca: es lo que identifica al
            // proveedor de un vistazo. El texto y el borde van a la escala de
            // tinta para no competir con la accion principal.
            startIcon={
              discordLoading ? null : <FaDiscord style={{ fontSize: '1.15rem', color: DISCORD }} />
            }
            sx={{
              py: 1.25,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: INK.primaria,
              borderColor: INK.borde,
              '&:hover': {
                backgroundColor: INK.hover,
                borderColor: INK.bordeFuerte,
              },
            }}
          >
            {discordLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Continue with Discord'
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
